import * as api from '../../api';
import * as viewerActions from './actions';
import { getSortingFieldKey, getSortingOrder } from '../sorting/selectors';
import { getCache, getData, getHasNextPage, getScrollHash } from './selectors';
import { loadWithPagination } from '../finder/listeners';
import { getAssetsFilter, getUser } from '../app/selectors';
import { getViewMode } from '../viewMode/selectors';
import { ViewMode } from '../viewMode/model';
import { AssetsData } from '../../api';
import * as lastStateActions from '../lastState/actions';
import * as appActions from '../app/actions';
import { EMPTY_TOTAL } from '../finder/model';
import { buildCacheKey } from '../../cache';
import { takeLatest, takeEvery, takeOnce } from '../../listenerMiddleware';
import {
  dropForeignViewerCaches,
  loadPersistedViewerCache,
  removePersistedViewerEntry,
  savePersistedViewerEntry,
} from './persistentCache';
import { debounce } from 'lodash';

export const viewerListeners = () => {
  watchLoadAssetsRequested();
  watchLastState();
  watchCacheHydration();
  watchCachePersistence();
};

function watchLoadAssetsRequested() {
  takeLatest(viewerActions.loadAssetsRequested, async ({ payload }, listenerApi) => {
    const { getState } = listenerApi;
    const { mode, force } = payload;

    const errorState: AssetsData = { assets: [], total: EMPTY_TOTAL };

    const sortingOrder = getSortingOrder(getState());
    const sortingFieldKey = getSortingFieldKey(getState());
    const viewMode = getViewMode(getState());
    const filter = getAssetsFilter(getState());

    // Эффективные параметры запроса без offset — они же образуют ключ кэша локации.
    const requestParams = {
      ...filter,
      orderBy: sortingFieldKey,
      ordering: sortingOrder,
      ...(viewMode === ViewMode.creatives
        ? { category: undefined, extension: undefined, show_creatives: true }
        : {}),
    };
    const cacheKey = buildCacheKey(requestParams);

    await loadWithPagination(
      {
        mode,
        getData,
        getScrollHash,
        setLoading: viewerActions.setLoading,
        assetsChanged: viewerActions.assetsChanged,
        errorMessage: 'Items have not been loaded',
        errorState,
        onRequest: (offset) => api.loadAssets({ ...requestParams, offset }),
        cacheKey,
        getHasNextPage,
        getCache,
        storeCache: viewerActions.cacheStored,
        invalidateCache: viewerActions.cacheInvalidated,
        force,
      },
      listenerApi,
    );
  });
}

function watchLastState() {
  takeLatest(viewerActions.creativeChanged, (action, { dispatch }) => {
    dispatch(lastStateActions.saveRequested());
  });
}

// Гидрация персистентного кэша из localStorage при старте (для залогиненного пользователя).
// userLoaded диспатчится в bootstrap (index.tsx) ДО первого loadAssetsRequested, поэтому к
// моменту первой загрузки списка кэш последней сессии уже в сторе. Для анонимных страниц
// (шаренные ссылки) userLoaded не диспатчится — гидрации нет, и это ожидаемо.
function watchCacheHydration() {
  takeOnce(appActions.userLoaded, ({ payload }, { dispatch }) => {
    const userId = String(payload.user.id);
    // Логаута в SPA нет, поэтому чужие кэши убираем здесь: за общей машиной мог сменить
    // пользователь, а в кэше лежат имена файлов и подписанные ссылки.
    dropForeignViewerCaches(userId);

    const cache = loadPersistedViewerCache(userId);
    if (Object.keys(cache).length > 0) {
      dispatch(viewerActions.cacheHydrated({ cache }));
    }
  });
}

// Персист кэша в localStorage. Пишем только изменившиеся локации (каждая — под своим
// ключом), с дебаунсом: при быстром скролле cacheStored летит на каждую подгруженную
// страницу, и писать хранилище на каждую незачем.
function watchCachePersistence() {
  const pendingKeys = new Set<string>();
  let flush: (() => void) | undefined;
  const scheduleFlush = debounce(() => flush?.(), 500);

  takeEvery(viewerActions.cacheStored, (action, { getState }) => {
    const user = getUser(getState());
    if (!user) {
      return;
    }
    pendingKeys.add(action.payload.key);
    // Записываем актуальное состояние на момент срабатывания дебаунса, а не на момент
    // экшена: за 500мс по этому же ключу могли подгрузиться ещё страницы.
    flush = () => {
      const cache = getCache(getState());
      const keys = [...pendingKeys];
      pendingKeys.clear();
      keys.forEach((key) => {
        const entry = cache[key];
        if (entry) {
          savePersistedViewerEntry(String(user.id), key, entry);
        }
      });
    };
    scheduleFlush();
  });

  takeEvery(viewerActions.cacheInvalidated, (action, { getState }) => {
    const user = getUser(getState());
    if (!user) {
      return;
    }
    pendingKeys.delete(action.payload.key);
    removePersistedViewerEntry(String(user.id), action.payload.key);
  });
}
